/**
 * Zeelool-style purchase wizard for the prescription-builder block.
 *
 * Standalone: it does NOT modify private state inside prescription-builder.js.
 * It wires the modal, step navigation, progress dots, the "Solo montura" shortcut and the
 * "Asesorame" WhatsApp button. All cart pricing/sync logic keeps working because every
 * selectable input keeps data-role="treatment-option" (or the original roles) and the
 * component reads them with this.querySelectorAll.
 *
 * Steps 1-4 are static markup. Steps 5 (Tratamientos), 6 (Sol con tu graduacion) and
 * 7 (NO LO OLVIDES) are built at runtime from the `lentes` collection by product handle.
 * The old treatments block inside step 4 is moved out ("Vision sencilla" stays in step 4).
 * Fotosensibles, Transitions, Drivewear, Polarizado and 8K Sun share the exclusive group
 * "photo": only one of them can be chosen.
 *
 * The standard antireflejo is selected automatically. Its product keeps a real price and a
 * Shopify automatic discount (100%) makes it free, so the cart shows it as a discount.
 */
(function () {
  var WHATSAPP = '18625297790';

  function initOne(root) {
    if (root.__prescriptionWizardInit) return;
    root.__prescriptionWizardInit = true;

    var q = function (ref) {
      return root.querySelector('[ref="' + ref + '"]');
    };

    var openWizardBtn = q('openWizardBtn');
    var frameOnlyBtn = q('frameOnlyBtn');
    var wizardModal = q('wizardModal');
    var wizardBack = q('wizardBack');
    var wizardClose = q('wizardClose');
    var wizardContinue = q('wizardContinue');
    var wizardDots = root.querySelectorAll('.prescription-builder__wizard-dot');
    var steps = root.querySelectorAll('.prescription-builder__wizard-step');
    var rxTypeSingleInput = q('rxTypeSingleInput');
    var rxTypeNoneInput = q('rxTypeNoneInput');
    var entryMethodLaterInput = q('entryMethodLaterInput');

    var treatReady = false;
    var sunReady = false;
    var extrasReady = false;

    function skipsRxStep() {
      return !!(entryMethodLaterInput && entryMethodLaterInput.checked);
    }

    function isPlano() {
      return !!(rxTypeNoneInput && rxTypeNoneInput.checked);
    }

    function skipsStep(n) {
      if (n === 2) return isPlano();
      if (n === 3) return isPlano() || skipsRxStep();
      if (n === 5) return !treatReady;
      if (n === 6) return !sunReady;
      if (n === 7) return !extrasReady;
      return false;
    }

    var totalSteps = steps.length || 1;
    var currentStep = 1;

    // ---- Catalog (matched by product handle) ----
    var NOMIX = 'No se combina con otros lentes fotocromáticos, polarizados ni 8K Sun.';
    var TREAT_CATALOG = [
      { handle: 'addon-antireflejo-standard', name: 'Antireflejo', label: 'Antireflejo', free: true, checked: true,
        desc: 'Selección automática: reduce el deslumbramiento y los reflejos molestos. El descuento se aplica solo en tu carrito.' },
      { handle: 'addon-blue-blocker', name: 'Blue block', label: 'Blue blocker',
        desc: 'Filtra parte de la luz azul. Lo amarás si usas pantallas a diario.' },
      { handle: 'addon-fotosensibles', name: 'Fotosensibles', label: 'Fotosensibles', group: 'photo',
        desc: 'Se oscurecen con el sol y se aclaran en interiores. Elige tu color. ' + NOMIX },
      { handle: 'addon-fotosmart-gris-marron', name: 'Transitions', label: 'Transitions', group: 'photo',
        desc: 'Se oscurecen automáticamente con el sol y vuelven a transparente en interiores. Elige gris o café. ' + NOMIX },
      { handle: 'addon-tinte-sol', name: 'Tinte de color', label: 'Tinte de color',
        desc: 'Combina tu receta con tu color favorito. Elige el color y si lo quieres en degradado.' }
    ];
    var SUN_CATALOG = [
      { handle: 'addon-polarizado', name: 'Polarizado', label: 'Polarizado', group: 'photo',
        desc: 'Elimina el deslumbramiento del asfalto, el agua y las superficies brillantes. Ideal para manejar. Elige tu color. Aviso: algunas pantallas (GPS, tablero, celular) pueden verse oscuras o con poco brillo según el ángulo. Si usas mucho las pantallas, elige 8K Sun Definition. ' + NOMIX },
      { handle: 'addon-drivewear', name: 'Drivewear', label: 'Drivewear', group: 'photo',
        desc: 'Fotocromático y polarizado en un solo lente: ajusta su oscuridad a la luz, incluso detrás del parabrisas. Al ser polarizado, aplica el mismo aviso de pantallas. ' + NOMIX + ' Entrega estimada: 20 días.' },
      { handle: 'addon-8k-sun-definition', name: '8K Sun Definition', label: '8K Sun Definition', group: 'photo',
        desc: 'No es el tinte: es alta definición en tu lente. Lente de sol sin polarizar, con diseño digital 8K. Elige tu color. Las pantallas se ven con normalidad. La opción para quien no quiere polarizado. ' + NOMIX }
    ];
    var EXTRAS_CATALOG = [
      { handle: 'addon-espejado', name: 'Espejado', label: 'Espejado',
        desc: 'Acabado reflectante por fuera del lente.', small: 'Entrega estimada: 20 días aprox.' },
      { handle: 'addon-antifog', name: 'Antifog', label: 'Antifog',
        desc: 'Ayuda a reducir el empañado o vaho que produce el cambio de temperaturas. Ideal para lentes para cocinar o con mascarilla.' },
      { handle: 'addon-antibacterial', name: 'Antibacterial', label: 'Antibacterial',
        desc: 'Nuestro tratamiento antibacterial con plata Ag+ ayuda con la higienización del lente. Perfecto para doctores, enfermeras y personal que necesite extra higiene en sus lentes.' }
    ];

    function el(tag, cls, text) {
      var n = document.createElement(tag);
      if (cls) n.className = cls;
      if (text) n.textContent = text;
      return n;
    }

    // Option panels (color / degradado) keep themselves in sync on every change.
    var syncers = [];
    function notifyChange() {
      syncers.forEach(function (f) { f(); });
      if (typeof root.handleLensSelectionChange === 'function') root.handleLensSelectionChange();
    }

    function money(n) {
      return n > 0 ? '$' + Number(n).toFixed(2) : 'Gratis';
    }

    // Exclusive groups across steps: registry of { input, off() }.
    var exclusive = {};
    function register(group, input, off) {
      if (!group) return;
      exclusive[group] = exclusive[group] || [];
      exclusive[group].push({ input: input, off: off });
    }
    function turnOffOthers(group, input) {
      if (!group) return;
      (exclusive[group] || []).forEach(function (other) {
        if (other.input !== input && other.input.checked) other.off();
      });
    }

    // Adds option selectors (color, degradado...) that resolve to a product variant.
    function attachOptions(entry, p, input, body) {
      var variants = p.variants || [];
      var opts = (p.options || []).filter(function (o) { return o.name !== 'Title'; });
      if (!opts.length) return null;

      var selects = [];
      var panel = el('span', 'prescription-builder__option-panel');
      panel.style.cssText = 'display:flex;flex-wrap:wrap;gap:8px;margin-top:8px;';
      panel.hidden = true;
      panel.addEventListener('click', function (e) { e.stopPropagation(); });
      opts.forEach(function (o, idx) {
        var wrap = el('span', '');
        wrap.style.cssText = 'display:flex;flex-direction:column;gap:2px;font-size:0.8em;';
        wrap.appendChild(el('span', '', o.name));
        var sel = document.createElement('select');
        sel.className = 'field__input';
        sel.style.cssText = 'min-width:130px;margin:0;';
        if (idx === 0) {
          var ph = document.createElement('option');
          ph.value = '';
          ph.textContent = 'Elige ' + o.name.toLowerCase();
          sel.appendChild(ph);
        }
        (o.values || []).forEach(function (v) {
          var op = document.createElement('option');
          op.value = v;
          op.textContent = v;
          sel.appendChild(op);
        });
        sel.addEventListener('change', resolve);
        selects.push(sel);
        wrap.appendChild(sel);
        panel.appendChild(wrap);
      });
      body.appendChild(panel);

      function clear() {
        selects.forEach(function (s, i) { if (i === 0) s.selectedIndex = 0; });
        input.value = '';
        input.setAttribute('data-price', '0');
        input.setAttribute('data-label', entry.label);
      }

      function resolve() {
        var vals = selects.map(function (s) { return s.value; });
        var match = null;
        if (vals[0]) {
          match = variants.filter(function (v) {
            return v.option1 === vals[0] && (!opts[1] || v.option2 === vals[1]) && (!opts[2] || v.option3 === vals[2]);
          })[0] || null;
        }
        if (match) {
          input.value = String(match.id);
          input.setAttribute('data-price', String(Number(match.price) || 0));
          input.setAttribute('data-label', entry.label + ' (' + vals.filter(Boolean).join(' / ') + ')');
        } else {
          input.value = '';
          input.setAttribute('data-price', '0');
          input.setAttribute('data-label', entry.label);
        }
        notifyChange();
      }

      function sync() {
        panel.hidden = !input.checked;
        selects[0].required = input.checked;
      }
      syncers.push(sync);
      return { resolve: resolve, clear: clear };
    }

    // One selectable card: radio (sun), checkbox (treatments / extras).
    function makeCard(type, groupName, entry, p, noneInput) {
      var variant = p.variants[0];
      var price = Number(variant.price) || 0;
      var label = el('label', 'prescription-builder__card');
      var input = document.createElement('input');
      input.type = type;
      if (groupName) input.name = groupName;
      input.setAttribute('data-role', 'treatment-option');
      input.setAttribute('data-label', entry.label);

      var body = el('span', 'prescription-builder__card-body');
      var title = el('span', 'prescription-builder__card-title');
      if (entry.free && price > 0) {
        // Real price crossed out + free: the automatic discount applies it in the cart.
        title.appendChild(document.createTextNode(entry.name + ' — '));
        var was = document.createElement('s');
        was.textContent = '$' + price.toFixed(2);
        title.appendChild(was);
        title.appendChild(document.createTextNode(' Gratis'));
      } else {
        title.textContent = entry.name + ' — ' + money(price);
      }
      body.appendChild(title);
      body.appendChild(el('span', 'prescription-builder__card-desc', entry.desc));
      if (entry.small) {
        var sm = el('span', 'prescription-builder__card-desc', entry.small);
        sm.style.fontSize = '0.72em';
        body.appendChild(sm);
      }

      var opt = attachOptions(entry, p, input, body);
      input.value = opt ? '' : String(variant.id);
      // Free (auto-discounted) items count as $0 in the on-page lens total.
      input.setAttribute('data-price', opt || entry.free ? '0' : String(price));

      input.addEventListener('change', function () {
        if (input.checked) turnOffOthers(entry.group, input);
        if (opt && input.checked) opt.resolve();
        else {
          if (opt) opt.clear();
          notifyChange();
        }
      });

      register(entry.group, input, function () {
        if (type === 'radio' && noneInput) noneInput.checked = true;
        else input.checked = false;
        if (opt) opt.clear();
        notifyChange();
      });

      if (entry.checked) input.checked = true;
      label.appendChild(input);
      label.appendChild(body);
      return label;
    }

    function makeNoneCard(groupName, title, desc) {
      var label = el('label', 'prescription-builder__card');
      var input = document.createElement('input');
      input.type = 'radio';
      input.name = groupName;
      input.setAttribute('data-role', 'treatment-option');
      input.value = '';
      input.setAttribute('data-price', '0');
      input.setAttribute('data-label', '');
      input.checked = true;
      input.addEventListener('change', notifyChange);
      var body = el('span', 'prescription-builder__card-body');
      body.appendChild(el('span', 'prescription-builder__card-title', title));
      body.appendChild(el('span', 'prescription-builder__card-desc', desc));
      label.appendChild(input);
      label.appendChild(body);
      return { label: label, input: input };
    }

    // Keep "Vision sencilla" in step 4 and remove the old treatments block from it.
    function prepareStep4() {
      var coating = root.querySelector('[ref="coatingGroup"]');
      if (!coating) return;
      var subs = coating.querySelectorAll('.prescription-builder__lens-subgroup');
      var treat = null;
      for (var i = 0; i < subs.length; i++) {
        if (subs[i].getAttribute('ref') !== 'materialBlock' && subs[i].querySelector('[data-role="treatment-option"]')) treat = subs[i];
      }
      if (!treat) return;
      var simple = treat.querySelector('[data-role="treatment-option"][data-mono-only="true"]');
      var simpleLabel = simple ? simple.closest('label') : null;
      if (simpleLabel) {
        var sub = el('div', 'prescription-builder__lens-subgroup');
        sub.appendChild(el('p', 'prescription-builder__lens-group-heading', 'Tu lente'));
        var grp = el('div', 'prescription-builder__card-group');
        grp.setAttribute('role', 'group');
        grp.appendChild(simpleLabel);
        sub.appendChild(grp);
        coating.insertBefore(sub, coating.firstChild);
      }
      treat.parentNode.removeChild(treat);
    }

    function buildSteps(products) {
      var byHandle = {};
      products.forEach(function (p) { byHandle[p.handle] = p; });
      function usable(entry) {
        var p = byHandle[entry.handle];
        var v = p && p.variants && p.variants[0];
        if (!v) return null;
        if (entry.free || Number(v.price) > 0) return p;
        return null;
      }

      var wizardBody = root.querySelector('.prescription-builder__wizard-body');
      var dotsWrap = root.querySelector('.prescription-builder__wizard-dots');
      if (!wizardBody) return;

      function addStep(num, heading, groupHeading, kind, items, none) {
        var list = items.filter(usable);
        if (!list.length) return false;
        var stepEl = el('div', 'prescription-builder__wizard-step');
        stepEl.setAttribute('data-step', String(num));
        stepEl.hidden = true;
        var wrap = el('div', 'prescription-builder__lens-selection');
        wrap.appendChild(el('p', 'prescription-builder__section-heading', heading));
        wrap.appendChild(el('p', 'prescription-builder__lens-group-heading', groupHeading));
        var g = el('div', 'prescription-builder__card-group');
        g.setAttribute('role', kind === 'radio' ? 'radiogroup' : 'group');
        var groupName = 'rx-step-' + num + '-' + (root.id || 'rx');
        var noneInput = null;
        if (none) {
          var noneCard = makeNoneCard(groupName, none.title, none.desc);
          noneInput = noneCard.input;
          g.appendChild(noneCard.label);
        }
        list.forEach(function (e) {
          g.appendChild(makeCard(kind === 'radio' ? 'radio' : 'checkbox', kind === 'radio' ? groupName : null, e, usable(e), noneInput));
        });
        wrap.appendChild(g);
        stepEl.appendChild(wrap);
        wizardBody.appendChild(stepEl);
        if (dotsWrap) {
          var dot = el('span', 'prescription-builder__wizard-dot');
          dot.setAttribute('data-dot', String(num));
          dotsWrap.appendChild(dot);
        }
        return true;
      }

      treatReady = addStep(5, 'Tratamientos', 'Combínalos como prefieras', 'treat', TREAT_CATALOG, null);
      if (treatReady) prepareStep4();
      sunReady = addStep(6, 'Sol con tu graduación', 'Protección solar — elige una opción', 'radio', SUN_CATALOG,
        { title: 'Sin protección solar', desc: 'Continuar con los lentes que elegiste.' });
      extrasReady = addStep(7, 'NO LO OLVIDES', 'Eleva tu estilo y maximiza tu visión con:', 'checkbox', EXTRAS_CATALOG, null);

      if (treatReady || sunReady || extrasReady) {
        steps = root.querySelectorAll('.prescription-builder__wizard-step');
        wizardDots = root.querySelectorAll('.prescription-builder__wizard-dot');
        var maxStep = 1;
        for (var i = 0; i < steps.length; i++) {
          maxStep = Math.max(maxStep, Number(steps[i].getAttribute('data-step')) || 1);
        }
        totalSteps = maxStep;
        showStep(currentStep);
      }
    }

    function lastStep() {
      var n = totalSteps;
      while (n > 1 && skipsStep(n)) n--;
      return n;
    }

    function showStep(n) {
      currentStep = n;
      for (var i = 0; i < steps.length; i++) {
        var stepEl = steps[i];
        var stepNum = Number(stepEl.getAttribute('data-step'));
        stepEl.hidden = stepNum !== n;
      }
      for (var d = 0; d < wizardDots.length; d++) {
        var dot = wizardDots[d];
        var dotNum = Number(dot.getAttribute('data-dot'));
        dot.classList.toggle('is-active', dotNum === n);
      }
      var scroller = root.querySelector('.prescription-builder__wizard-body');
      if (scroller) scroller.scrollTop = 0;
      if (wizardBack) wizardBack.hidden = n === 1;
      if (wizardContinue) wizardContinue.textContent = n >= lastStep() ? 'Listo' : 'Continuar';
    }

    function openModal() {
      root.dataset.frameOnly = 'false';
      if (rxTypeNoneInput && rxTypeNoneInput.checked && rxTypeSingleInput) {
        rxTypeSingleInput.checked = true;
        rxTypeSingleInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
      showStep(1);
      if (wizardModal) wizardModal.hidden = false;
      document.body.style.overflow = 'hidden';
    }

    function closeModal() {
      if (wizardModal) wizardModal.hidden = true;
      document.body.style.overflow = '';
    }

    function selectFrameOnly() {
      root.dataset.frameOnly = 'true';
      if (rxTypeNoneInput) {
        rxTypeNoneInput.checked = true;
        rxTypeNoneInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    if (openWizardBtn) openWizardBtn.addEventListener('click', openModal);
    if (frameOnlyBtn) frameOnlyBtn.addEventListener('click', selectFrameOnly);
    if (wizardClose) wizardClose.addEventListener('click', closeModal);
    if (wizardBack) {
      wizardBack.addEventListener('click', function () {
        var prev = currentStep - 1;
        while (prev > 1 && skipsStep(prev)) prev--;
        if (prev >= 1) showStep(prev);
      });
    }

    // ---- "Asesorame" -> WhatsApp (entry area + wizard footer) ----
    // Summary of what the customer has already chosen (rx type, progressive base,
    // thickness, treatments, sun lens, extras).
    function selectionSummary() {
      var parts = [];
      var rx = root.querySelector('input[name*="Tipo de receta"]:checked');
      if (rx && rx.value) parts.push('Receta: ' + rx.value);
      ['progressive-base-option', 'material-option', 'treatment-option'].forEach(function (role) {
        root.querySelectorAll('[data-role="' + role + '"]:checked').forEach(function (i) {
          var l = i.getAttribute('data-label');
          if (l) parts.push(l);
        });
      });
      return parts.join(', ');
    }

    function advisorHref() {
      var inWizard = wizardModal && !wizardModal.hidden;
      var chosen = selectionSummary();
      var msg = 'Hola LaOpttica, necesito que me asesoren con mis lentes mientras compro.' +
        ' Estoy viendo: ' + document.title + ' ' + window.location.href +
        (inWizard ? ' (voy en el paso ' + currentStep + ' del asistente de lentes)' : '') +
        (chosen ? '. Hasta ahora elegi: ' + chosen : '');
      return 'https://wa.me/' + WHATSAPP + '?text=' + encodeURIComponent(msg);
    }

    function makeAdvisor(cls, style) {
      var a = document.createElement('a');
      a.className = cls;
      a.textContent = 'Asesórame';
      a.href = advisorHref();
      a.target = '_blank';
      a.rel = 'noopener';
      a.setAttribute('aria-label', 'Asesórame por WhatsApp');
      if (style) a.style.cssText = style;
      var refresh = function () { a.href = advisorHref(); };
      a.addEventListener('click', refresh);
      a.addEventListener('mouseenter', refresh);
      a.addEventListener('touchstart', refresh, { passive: true });
      return a;
    }

    var entryWrap = q('entryButtons');
    if (entryWrap) {
      entryWrap.appendChild(makeAdvisor('prescription-builder__entry-btn prescription-builder__entry-btn--secondary', 'text-align:center;text-decoration:none;display:inline-block;'));
    }
    var footer = root.querySelector('.prescription-builder__wizard-footer');
    if (footer) {
      footer.appendChild(makeAdvisor('prescription-builder__advisor', 'display:block;text-align:center;margin-top:10px;padding:0.9em;border-radius:var(--style-border-radius-buttons, 30px);border:1px solid var(--color-logo, var(--color-foreground));color:var(--color-logo, var(--color-foreground));text-decoration:none;font-size:0.85rem;text-transform:uppercase;letter-spacing:0.04em;'));
    }

    /**
     * Native validation only reports on controls the browser can focus, so each step
     * is validated on its own and the first invalid visible field is reported.
     */
    function findInvalidField(stepNum) {
      var step = root.querySelector('.prescription-builder__wizard-step[data-step="' + stepNum + '"]');
      if (!step) return null;
      var fields = step.querySelectorAll('input, select, textarea');
      for (var i = 0; i < fields.length; i++) {
        var field = fields[i];
        if (field.disabled || !field.willValidate) continue;
        var node = field.parentElement;
        var inHiddenBlock = false;
        while (node && node !== step) {
          if (node.hidden) {
            inHiddenBlock = true;
            break;
          }
          node = node.parentElement;
        }
        if (inHiddenBlock) continue;
        if (!field.checkValidity()) return field;
      }
      return null;
    }

    function reportField(field) {
      if (typeof field.focus === 'function') field.focus({ preventScroll: false });
      if (typeof field.reportValidity === 'function') field.reportValidity();
    }

    if (wizardContinue) {
      wizardContinue.addEventListener('click', function () {
        var invalid = findInvalidField(currentStep);
        if (invalid) {
          reportField(invalid);
          return;
        }

        if (currentStep < lastStep()) {
          var next = currentStep + 1;
          while (next < totalSteps && skipsStep(next)) next++;
          showStep(next);
          return;
        }

        for (var s = 1; s < totalSteps; s++) {
          if (skipsStep(s)) continue;
          var earlier = findInvalidField(s);
          if (earlier) {
            showStep(s);
            reportField(earlier);
            return;
          }
        }

        var productForm = root.dataset.productFormId ? document.getElementById(root.dataset.productFormId) : null;
        if (productForm && typeof productForm.checkValidity === 'function' && !productForm.checkValidity()) {
          productForm.reportValidity();
          return;
        }

        closeModal();
        if (productForm) {
          if (typeof productForm.requestSubmit === 'function') {
            productForm.requestSubmit();
          } else {
            productForm.submit();
          }
        }
      });
    }

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && wizardModal && !wizardModal.hidden) {
        closeModal();
      }
    });

    showStep(1);

    fetch('/collections/lentes/products.json?limit=250', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : { products: [] }; })
      .then(function (data) { buildSteps(data.products || []); })
      .catch(function () {});

    window.setTimeout(function () {
      root.classList.remove('is-loading');
    }, 400);
  }

  function boot() {
    document.querySelectorAll('prescription-builder-component').forEach(initOne);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', boot);
})();
